package org.vreo.devicehub.features.roi.presentation

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.*
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AlignHorizontalCenter
import androidx.compose.material.icons.filled.DirectionsCar
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material.icons.outlined.AlignHorizontalCenter
import androidx.compose.material.icons.outlined.CenterFocusStrong
import androidx.compose.material.icons.outlined.CenterFocusWeak
import androidx.compose.material.icons.outlined.CropSquare
import androidx.compose.material.icons.outlined.Square
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle

import org.jetbrains.compose.resources.stringResource

import devicehub.composeapp.generated.resources.*
import org.vreo.devicehub.features.core.domain.device.roi.Roi
import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.roi.presentation.components.RoiList
import org.vreo.devicehub.features.roi.presentation.components.drawRoi

/**
 * Region of interest (ROI) selection root screen
 * @param viewModel view model for region of interest selection screen
 */
@Composable
fun RoiScreenRoot(
    viewModel: RoiViewModel
) {
    val state: RoiState by viewModel.state.collectAsStateWithLifecycle()

    RoiScreenView(
        state = state,
        onAction = { action ->
            viewModel.onAction(action)
        }
    )
}

/**
 * Region of interest selection view, allows user to apply and update ROIs
 * @param state view state
 * @param onAction action handler
 * @param scrollState scroll state
 */
@Composable
fun RoiScreenView(
    state: RoiState,
    onAction: (RoiActions) -> Unit,
    scrollState: ScrollState = rememberScrollState()
) {
    val points = remember {
        mutableStateListOf(
            Offset(0.1f, 0.1f),
            Offset(0.4f, 0.1f),
            Offset(0.4f, 0.4f),
            Offset(0.1f, 0.4f)
        )
    }
    var draggingIndex by remember { mutableStateOf(-1) }
    var loadedRoiId by remember { mutableStateOf<Int?>(null) }
    val textMeasurer = rememberTextMeasurer()

    // Sync local points when selected ROI changes in the view model
    LaunchedEffect(state.selectedRoi?.id) {
        state.selectedRoi?.let { roi ->
            if (roi.id != loadedRoiId) {
                points.clear()
                points.addAll(roi.points)
                loadedRoiId = roi.id
            }
        } ?: run { loadedRoiId = null }
    }

    // Handling Dialogs
    if (state.isLoading) LoadingDialog()
    state.error?.let { error ->
        ErrorDialog(
            dialogTitle = stringResource(Res.string.error_general_title),
            dialogText = error.asString(),
            onDismissRequest = { onAction(RoiActions.OnClearError) },
            onConfirmation = { onAction(RoiActions.OnClearError) }
        )
    }

    // Main Screen
    Column(
        modifier = Modifier
            .fillMaxHeight()
            .widthIn(max = 1400.dp)
            .verticalScroll(scrollState)
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        // Screen title with icon
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Icon(
                imageVector = Icons.Outlined.CenterFocusWeak,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp)
            )
            Text(
                text = stringResource(Res.string.roi_screen_title),
                style = MaterialTheme.typography.headlineSmall,
            )
        }

        // Status Warning, only shows if device is inactive
        if (!state.isDeviceActive) {
            Surface(
                color = MaterialTheme.colorScheme.errorContainer,
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Default.ErrorOutline, contentDescription = null)
                    Spacer(Modifier.width(12.dp))
                    Text(
                        text = stringResource(Res.string.device_control_unavailable_label),
                        style = MaterialTheme.typography.labelMedium
                    )
                }
            }
        }

        // Request new image
        OutlinedCard(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.dp),
                modifier = Modifier.padding(12.dp),
            ) {
                // Save button
                Button(
                    enabled = state.isDeviceActive,
                    onClick = { onAction(RoiActions.OnSaveRoi) },
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(12.dp)
                ) {
                    Text(stringResource(Res.string.save_button_title))
                }

                // Refresh button
                OutlinedButton(
                    enabled = state.isDeviceActive,
                    onClick = { onAction(RoiActions.OnRefreshImage) },
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(12.dp)
                ) {
                    Text(stringResource(Res.string.roi_refresh_image_button_label))
                }

                HorizontalDivider(modifier = Modifier.fillMaxWidth())

                // Add ROI button
                OutlinedButton(
                    enabled = state.isDeviceActive,
                    onClick = { onAction(RoiActions.OnAddRoi) },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(stringResource(Res.string.roi_add_roi_button_label))
                }

                AnimatedVisibility(visible = state.roi.isNotEmpty()) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 8.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.roi_screen_list_title),
                            style = MaterialTheme.typography.titleMedium,
                            modifier = Modifier.padding(bottom = 12.dp)
                        )
                        HorizontalDivider(modifier = Modifier.fillMaxWidth())

                        RoiList(
                            items = state.roi,
                            selectedId = state.selectedRoi?.id,
                            onClick = { selectedRoi ->
                                if (selectedRoi.id == state.selectedRoi?.id) {
                                    return@RoiList
                                }

                                // Store current item
                                state.selectedRoi?.let { currentRoi ->
                                    onAction(RoiActions.OnUpdateRoiPoints(currentRoi.id, points.toList()))
                                }

                                // Select new item
                                onAction(RoiActions.OnClickedRoi(selectedRoi))
                                points.clear()
                                points.addAll(selectedRoi.points)
                            },
                            onDelete = { onAction(RoiActions.OnDeleteRoi(it.id)) },
                        )
                    }
                }
            }
        }

        // The image Container
        ElevatedCard(
            shape = RoundedCornerShape(12.dp),
            elevation = CardDefaults.elevatedCardElevation(defaultElevation = 2.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                modifier = Modifier.fillMaxSize().padding(12.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // This Box acts as the "Coordinate Space" for the Image. Full-HD image is
                // expected, keeping image and canvas aspect ratio same guaranties that
                // ROI coordinates are tracked correctly.
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .aspectRatio(1920f / 1080f)
                ) {
                    if (state.image == null) {
                        Text(
                            text = stringResource(Res.string.message_details_image_not_available_title),
                            style = MaterialTheme.typography.displaySmall,
                            modifier = Modifier
                                .align(Alignment.Center)
                                .fillMaxSize()
                        )
                    } else {
                        Image(
                            bitmap = state.image,
                            contentDescription = null,
                            contentScale = ContentScale.FillBounds,
                            modifier = Modifier.fillMaxSize()
                        )

                        // Canvas to draw the ROI.
                        //
                        // Point interrupts allow to detect selected ROI point. Area is selected,
                        // nearest ROI point within the 60 mixels will be activated and its position
                        // will be updated. Once point is no longer selected, dragging index is set
                        // to -1 to indicate that no point is selected.
                        Canvas(
                            modifier = Modifier
                                .matchParentSize()
                                .pointerInput(state.selectedRoi?.id) {
                                    detectDragGestures(
                                        onDragStart = { startOffset ->
                                            draggingIndex = points.indexOfFirst { p ->
                                                val screenPoint = Offset(p.x * size.width, p.y * size.height)
                                                (screenPoint - startOffset).getDistance() < 60f
                                            }
                                        },
                                        onDrag = { change, dragAmount ->
                                            if (draggingIndex != -1) {
                                                val currentPoint = points[draggingIndex]
                                                points[draggingIndex] = Offset(
                                                    x = (currentPoint.x + dragAmount.x / size.width).coerceIn(0f, 1f),
                                                    y = (currentPoint.y + dragAmount.y / size.height).coerceIn(0f, 1f)
                                                )
                                                change.consume()
                                            }
                                        },
                                        onDragEnd = {
                                            // Update ViewModel ONLY when drag finishes
                                            state.selectedRoi?.let { roi ->
                                                onAction(RoiActions.OnUpdateRoiPoints(roi.id, points.toList()))
                                            }
                                            draggingIndex = -1
                                        }
                                    )
                                }
                        ) {
                            // Draw all available ROIs. Selected ROI is the only ROI that allows user to update its
                            // coordinates (points).
                            state.roi.forEach { roi ->
                                val selected: Boolean = (roi.id == state.selectedRoi?.id)

                                val drawingPoints = if (selected) points else roi.points
                                val selectedPointIndex = if (selected) draggingIndex else -1
                                val drawingColor = if (selected) Color(0xFF59F359) else Color(0xFF2C2C58)

                                drawRoi(
                                    roiPoints = drawingPoints,
                                    draggingIndex = selectedPointIndex,
                                    roiColor = drawingColor,
                                    label = roi.id.toString(),
                                    textMeasurer = textMeasurer
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Preview
@Composable
fun RoiScreenViewPreview() {
    val rois = listOf(
        Roi(
            id = 0,
            label = "Bay-1"
        ),
        Roi(
            id = 1,
            label = "Bay-2",
            points = listOf(
                Offset(0.5f, 0.2f),
                Offset(0.7f, 0.2f),
                Offset(0.7f, 0.4f),
                Offset(0.5f, 0.4f)
            )
        )
    )

    MaterialTheme {
        Scaffold {
            RoiScreenView(
                state = RoiState(
                    selectedRoi = rois.first(),
                    roi = rois
                ),
                onAction = {},
            )
        }
    }
}
